import { apiService } from "./api";
import { PaymentTransaction, PaymentMethod } from "../types";

interface CreatePaymentRequest {
    amount: number;
    payment_method: PaymentMethod;
    reference_number?: string;
    notes?: string;
    account_id?: string | null;
}

/** A payment as edited in a form. One that already exists carries its
 * transaction_id, so saving can tell it apart from a new one. */
export interface EditablePayment extends CreatePaymentRequest {
    transaction_id?: string;
}

const toCents = (amount: unknown) => Math.round(Number(amount) * 100);

/** What a PATCH /payments/{id} may change. null clears reference/notes/account. */
type PaymentChanges = {
    amount?: number;
    payment_method?: PaymentMethod;
    reference_number?: string | null;
    notes?: string | null;
    account_id?: string | null;
};

/** The fields where an edited payment differs from its saved version. */
function changedFields(edited: EditablePayment, saved: PaymentTransaction): PaymentChanges {
    const changes: PaymentChanges = {};
    if (toCents(edited.amount) !== toCents(saved.amount)) changes.amount = Number(edited.amount);
    if (edited.payment_method !== saved.payment_method) changes.payment_method = edited.payment_method;
    if ((edited.reference_number || "") !== (saved.reference_number || "")) {
        changes.reference_number = edited.reference_number || null;
    }
    if ((edited.account_id || null) !== (saved.account_id || null)) changes.account_id = edited.account_id || null;
    if ((edited.notes || "") !== (saved.notes || "")) changes.notes = edited.notes || null;
    return changes;
}

/** Did the server really save these changes? An older API only edits notes
 * (and can't clear them), silently ignoring the rest. */
const changesApplied = (result: PaymentTransaction, changes: PaymentChanges) =>
    (changes.amount === undefined || toCents(result.amount) === toCents(changes.amount)) &&
    (changes.payment_method === undefined || result.payment_method === changes.payment_method) &&
    (changes.reference_number === undefined || (result.reference_number || null) === changes.reference_number) &&
    (changes.account_id === undefined || (result.account_id || null) === changes.account_id) &&
    (changes.notes === undefined || (result.notes || null) === changes.notes);

const withoutId = ({ transaction_id, ...payment }: EditablePayment): CreatePaymentRequest => payment;

class PaymentService {
    async createPayment(
        orderId: string,
        payment: CreatePaymentRequest
    ): Promise<PaymentTransaction> {
        return apiService.post<PaymentTransaction>(
            `/orders/${orderId}/payments`,
            payment
        );
    }

    async getOrderPayments(orderId: string): Promise<PaymentTransaction[]> {
        return apiService.get<PaymentTransaction[]>(
            `/orders/${orderId}/payments`
        );
    }

    async deletePayment(transactionId: string): Promise<void> {
        return apiService.delete(`/payments/${transactionId}`);
    }

    /** Edit a saved payment in place — it keeps its original date. */
    async updatePayment(transactionId: string, changes: PaymentChanges): Promise<PaymentTransaction> {
        return apiService.patch<PaymentTransaction>(`/payments/${transactionId}`, changes);
    }

    /**
     * Make the order's payments match an edited list, touching only what
     * changed — so saving an order (e.g. just a new address) never re-dates
     * the payments on it:
     *  - a payment kept as it was is left alone;
     *  - one that was edited (amount, method, reference, account or notes)
     *    is updated in place and keeps its original date, who recorded it,
     *    and any refund/return links;
     *  - ones taken off the list are removed, ones without a transaction_id
     *    are added.
     * Removals go first and amount decreases before increases, so the API's
     * "more than the order total" check never trips half-way. Nothing is
     * changed if the edits include a zero/negative amount (e.g. an edited
     * refund) — the API only accepts positive payments.
     */
    async syncOrderPayments(orderId: string, edited: EditablePayment[]): Promise<void> {
        const saved = await this.getOrderPayments(orderId);
        const savedById = new Map(saved.map((p) => [p.transaction_id, p]));

        const keptIds = new Set<string>();
        const toAdd: CreatePaymentRequest[] = [];
        const toEdit: { transactionId: string; changes: PaymentChanges; centsDelta: number }[] = [];

        for (const payment of edited) {
            const current = payment.transaction_id ? savedById.get(payment.transaction_id) : undefined;
            if (!current || keptIds.has(current.transaction_id)) {
                toAdd.push(withoutId(payment));
                continue;
            }
            keptIds.add(current.transaction_id);
            const changes = changedFields(payment, current);
            if (Object.keys(changes).length > 0) {
                toEdit.push({
                    transactionId: current.transaction_id,
                    changes,
                    centsDelta: toCents(payment.amount) - toCents(current.amount),
                });
            }
        }
        const toRemove = saved.filter((p) => !keptIds.has(p.transaction_id));

        const nonPositive =
            toAdd.some((p) => !(Number(p.amount) > 0)) ||
            toEdit.some(({ changes }) => changes.amount !== undefined && !(changes.amount > 0));
        if (nonPositive) {
            throw new Error(
                "Refunds and other zero/negative amounts can't be added or edited here — nothing was changed.",
            );
        }

        for (const payment of toRemove) {
            await this.deletePayment(payment.transaction_id);
        }
        for (const { transactionId, changes } of [...toEdit].sort((a, b) => a.centsDelta - b.centsDelta)) {
            const result = await this.updatePayment(transactionId, changes);
            if (!changesApplied(result, changes)) {
                throw new Error(
                    "The server didn't save the payment change — the API needs updating to the latest version first.",
                );
            }
        }
        for (const payment of toAdd) {
            await this.createPayment(orderId, payment);
        }
    }
}

export const paymentService = new PaymentService();
